import { z } from "zod"

import { propertyDataSchema } from "./property-data"
import { tariffDocumentSchema } from "./tariff-document"
import { tariffRateSchema } from "./tariff-rate"

/**
 * Tariff object data structure.
 */
export const tariffSchema = z.object({
  /**
   * Unique Genability ID (primary key) for this tariff.
   */
  tariffId: z.number(),
  /**
   * Unique Genability ID that persists across all revisions of this tariff.
   */
  masterTariffId: z.number().positive().int(),
  /**
   * Short code that the LSE uses as an alternate name for the tariff.
   */
  tariffCode: z.string(),
  /**
   * Name of the tariff as used by the LSE.
   */
  tariffName: z.string(),
  /**
   * Name of the tariff as it appears in the tariff document.
   */
  tariffBookName: z.string().nullable().optional(),
  /**
   * ID of load serving entity this tariff belongs to.
   */
  lseId: z.number().positive().int(),
  /**
   * Name of the load serving entity.
   */
  lseName: z.string(),
  /**
   * Abbreviated name of the load serving entity.
   */
  lseCode: z.string().optional(),
  /**
   * Type of service for the tariff.
   * Current values include ELECTRICITY (grid power),
   * SOLAR_PV (for a Solar PPA, Lease or similar),
   * and GAS (Natural Gas).
   */
  serviceType: z.string().optional(),
  /**
   * Unique Genability ID that identifies the prior revision of the
   * tariffId above.
   */
  priorTariffId: z.number().positive().int().optional(),
  /**
   * In states like Texas where the load serving entity that sells the power
   * is different
   * than the load serving entity that distributes the power,
   * this will contain the ID of the distribution LSE.
   * Otherwise, it will be null.
   */
  distributionLseId: z.number().positive().int().optional(),
  /**
   * Possible values are:
   * - DEFAULT: tariff that is automatically given to this service class
   * - ALTERNATE: opt-in alternate tariff for this service class
   * - OPTIONAL_EXTRA: opt-in extra, such as green power or a smart thermostat
   * program
   * - RIDER: charge that can apply to multiple tariffs. Often a regulatory
   * mandated charge
   */
  tariffType: z.string().optional(),
  /**
   * Possible values are:
   * - RESIDENTIAL: homes, apartments, etc.
   * - GENERAL: commercial, industrial and other business and organization
   * service types
   * (often have additional applicability criteria)
   * - SPECIAL_USE: examples are government, agriculture, street lighting,
   * transportation
   * - PROPOSED: Utility rates that have been proposed by utilities and approved
   * by utility commissions,
   * but are not yet effective (requires product subscription)
   */
  customerClass: z.string().nullable().optional(),
  /**
   * Number of customers that are on this master tariff.
   */
  customerCount: z.number().int().optional(),
  /**
   * The likelihood that a customer is on this tariff of all the tariffs
   * in the search results.
   * Only populated when getting more than one tariff.
   */
  customerLikelihood: z.number().nullable().optional(),
  /**
   * Where we got the customer count numbers from.
   * Typically FERC (form 1 filings) or Genability (our own estimates).
   */
  customerCountSource: z.string().optional(),
  /**
   * ID of the territory that this tariff applies to.
   * This is typically the service area for the LSE in this regulatory region
   * (i.e. a state in the USA).
   */
  territoryId: z.number().optional(),
  /**
   * Date on which the tariff was or will be effective.
   */
  effectiveDate: z.string().optional(),
  /**
   * Date on which this tariff is no longer effective.
   * Can be null which means end date is not known or tariff is open-ended.
   */
  endDate: z.string().nullable().optional(),
  /**
   * Date on which a tariff became closed to new customers, but still
   * available for customers who were on it at the time.
   * Can be null which means that the tariff is not closed.
   * All versions of a particular tariff (i.e. those that share a particular
   * masterTariffId) will have the same closedDate value.
   * See example.
   */
  closedDate: z.string().nullable().optional(),
  /**
   * Property of the tariff determining when the tariff takes effect.
   * Possible values: TARIFF_EFFECTIVE_DATE, BILLING_PERIOD_START.
   */
  effectiveOnRule: z.string().optional(),
  /**
   * If populated (usually is), it's the timezone that this tariff's dates and
   * times refer to.
   */
  timeZone: z.string().optional(),
  /**
   * How frequently bills are generated.
   */
  billingPeriod: z.string().optional(),
  /**
   * ISO Currency code that the rates for this tariff refer to
   * (e.g. USD for USA Dollar).
   */
  currency: z.string().optional(),
  /**
   * A comma-separated list of all the different ChargeType rates on this tariff
   * (see field on TariffRate below).
   */
  chargeTypes: z.string().optional(),
  /**
   * The most fine-grained period for which charges are calculated.
   */
  chargePeriod: z.string().optional(),
  /**
   * When applicable, the minimum monthly consumption allowed in order to be
   * eligible for this tariff.
   */
  minMonthlyConsumption: z.number().nullable().optional(),
  /**
   * When applicable, the maximum monthly consumption allowed in order to be
   * eligible for this tariff.
   */
  maxMonthlyConsumption: z.number().nullable().optional(),
  /**
   * When applicable, the minimum monthly demand allowed in order to be
   * eligible for this tariff.
   */
  minMonthlyDemand: z.number().nullable().optional(),
  /**
   * When applicable, the maximum monthly demand allowed in order to be
   * eligible for this tariff.
   */
  maxMonthlyDemand: z.number().nullable().optional(),
  /**
   * Indicates whether this tariff contains one or more Time of Use Rate.
   */
  hasTimeOfUseRates: z.boolean().optional(),
  /**
   * Indicates whether this tariff contains one or more Tiered Rate.
   */
  hasTieredRates: z.boolean().optional(),
  /**
   * Indicates whether this tariff contains one or more Rate that can be
   * contracted
   * (sometimes called bypassable or associated with a price to compare).
   */
  hasContractedRates: z.boolean().optional(),
  /**
   * Indicates that this tariff has additional eligibility criteria,
   * as specified in the TariffProperty collection (below).
   */
  hasTariffApplicability: z.boolean().optional(),
  /**
   * Indicates that one or more rates on this tariff are only applicable
   * to customers with a particular circumstance.
   * When true, this will be specified in the TariffProperty collection
   * and also on the TariffRate or rates in question.
   */
  hasRateApplicability: z.boolean().optional(),
  /**
   * Indicates whether this tariff contains one or more net metered rates.
   */
  hasNetMetering: z.boolean().nullable().optional(),
  /**
   * Privacy status of the tariff.
   * Possible values are PUBLIC, UNLISTED, or PRIVATE.
   */
  privacy: z.string().nullable().optional(),
  /**
   * DEPRECATED. Signifies that this tariff ID is the currently active tariff
   * (now) within all tariffs sharing this master tariff ID.
   * Instead, use the effective and end dates to test whether a tariff
   * is active or not.
   * If you're searching for tariffs, use effectiveOn instead.
   */
  isActive: z.boolean().optional(),
  /**
   * The properties on this tariff. See below for the TariffProperty structure.
   */
  properties: z.array(propertyDataSchema).optional(),
  /**
   * The rates for this tariff. See below for TariffRate structure.
   */
  rates: z.array(tariffRateSchema).optional().nullable(),
  /**
   * The documents for this tariff. See below for TariffDocument structure.
   */
  documents: z.array(tariffDocumentSchema).optional(),
})

export type Tariff = z.infer<typeof tariffSchema>
